import { useEffect, useMemo, useState } from 'react';
import type { UpdateAssistantSettingsInput } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { DataState } from '../../components/ui/DataState.js';
import { CheckboxField, SelectField, TextField } from '../../components/ui/Field.js';
import { TabNav } from '../../components/ui/TabNav.js';
import { InsightsUsagePanel } from '../insights-studio/InsightsUsagePanel.js';
import { useAssistantModels, useAssistantSettings, useUpdateAssistantSettings } from './api.js';

/**
 * Admin settings for the "Ask PSA" assistant: the Anthropic API key (write-only), the
 * Claude model id, and the on/off switch. The chat stays hidden from members until this
 * is configured and enabled.
 */
export function AssistantSettingsPage(): JSX.Element {
  const settingsQuery = useAssistantSettings();
  const updateMutation = useUpdateAssistantSettings();
  const settings = settingsQuery.data;
  const modelsQuery = useAssistantModels(Boolean(settings?.hasApiKey || settings?.configured));

  const [model, setModel] = useState('');
  const [fallbackModel, setFallbackModel] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [clearKey, setClearKey] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<'settings' | 'usage'>('settings');

  const modelOptions = useMemo(() => {
    const models = modelsQuery.data ?? [];
    const options = models.map((m) => ({
      value: m.id,
      label: m.displayName === m.id ? m.id : `${m.displayName} (${m.id})`,
    }));
    // Keep a stored model selectable even when Anthropic no longer lists it.
    if (model && !models.some((m) => m.id === model)) {
      options.unshift({ value: model, label: `${model} (current)` });
    }
    return options;
  }, [modelsQuery.data, model]);

  useEffect(() => {
    if (!settings) return;
    setModel(settings.model ?? '');
    setFallbackModel(settings.fallbackModel ?? '');
    setEnabled(settings.enabled);
    setApiKey('');
    setClearKey(false);
  }, [settings]);

  const handleSave = (): void => {
    setSaved(false);
    const input: UpdateAssistantSettingsInput = {
      model: model.trim() || null,
      fallbackModel: fallbackModel.trim() || null,
      enabled,
    };
    if (clearKey) {
      input.apiKey = null;
    } else if (apiKey.trim()) {
      input.apiKey = apiKey.trim();
    }
    updateMutation.mutate(input, {
      onSuccess: () => {
        setSaved(true);
        setApiKey('');
        setClearKey(false);
      },
    });
  };

  const saveError =
    updateMutation.error instanceof ApiError
      ? updateMutation.error.message
      : updateMutation.error
        ? 'Failed to save assistant settings.'
        : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Assistant"
        description="Configure the read-only 'Ask PSA' assistant (Anthropic Claude)."
      />

      <TabNav
        tabs={[
          { id: 'settings', label: 'Settings' },
          { id: 'usage', label: 'Insights usage' },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as 'settings' | 'usage')}
      />

      {tab === 'usage' ? (
        <InsightsUsagePanel />
      ) : (
      <DataState
        isLoading={settingsQuery.isLoading}
        isError={settingsQuery.isError}
        isEmpty={!settings}
      >
        {settings ? (
          <Card className="max-w-2xl space-y-4 p-6">
            <div className="flex flex-wrap gap-2">
              <Badge tone={settings.configured ? 'green' : 'gray'}>
                {settings.configured ? 'Configured' : 'Not configured'}
              </Badge>
              <Badge tone={settings.enabled ? 'green' : 'gray'}>
                {settings.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              {settings.managedInDatabase ? <Badge tone="blue">Managed in app</Badge> : null}
            </div>

            <p className="text-sm text-slate-600">
              The assistant answers questions about system data (scoped to each member&rsquo;s
              permissions) and how PSA works. It is strictly read-only.
            </p>

            <TextField
              label="Anthropic API key"
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder={settings.hasApiKey ? '•••••••• stored' : 'sk-ant-…'}
              disabled={clearKey}
              autoComplete="off"
            />
            {settings.hasApiKey ? (
              <CheckboxField
                label="Clear the stored API key"
                checked={clearKey}
                onChange={(event) => setClearKey(event.target.checked)}
              />
            ) : null}
            {!settings.canStoreSecrets ? (
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
                The server has no encryption key configured, so the API key can&rsquo;t be stored
                from the UI. Set it via the ANTHROPIC_API_KEY app setting instead.
              </p>
            ) : null}

            <TextField
              label="Claude model"
              value={model}
              onChange={(event) => setModel(event.target.value)}
              placeholder="claude-sonnet-4-5"
            />

            {modelsQuery.data && modelsQuery.data.length > 0 ? (
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <SelectField
                    label="Available models"
                    value={modelOptions.some((o) => o.value === model) ? model : ''}
                    onChange={(event) => {
                      if (event.target.value) setModel(event.target.value);
                    }}
                    options={modelOptions}
                    placeholder="Pick a model…"
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void modelsQuery.refetch()}
                  disabled={modelsQuery.isFetching}
                >
                  {modelsQuery.isFetching ? 'Refreshing…' : 'Refresh models'}
                </Button>
              </div>
            ) : null}
            {modelsQuery.isLoading ? (
              <p className="text-xs text-slate-500">Loading available models from Anthropic…</p>
            ) : null}
            {modelsQuery.isError ? (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 p-2">
                <p className="text-xs text-amber-700">
                  {modelsQuery.error instanceof ApiError
                    ? modelsQuery.error.message
                    : 'Could not load the model list from Anthropic. Type the model id manually.'}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void modelsQuery.refetch()}
                  disabled={modelsQuery.isFetching}
                >
                  {modelsQuery.isFetching ? 'Retrying…' : 'Retry'}
                </Button>
              </div>
            ) : null}
            {settings && !settings.hasApiKey && !settings.configured ? (
              <p className="text-xs text-slate-500">
                Save an Anthropic API key to load the list of available models.
              </p>
            ) : null}

            <TextField
              label="Fallback model"
              value={fallbackModel}
              onChange={(event) => setFallbackModel(event.target.value)}
              placeholder="claude-haiku-4-5"
            />
            <p className="text-xs text-slate-500">
              Used automatically when the primary model is still overloaded after retries, so an
              Anthropic outage degrades the answer instead of failing it. Pick a cheaper model
              (e.g. a Haiku id). Leave blank to disable.
            </p>

            <CheckboxField
              label="Enable the assistant for members"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />

            {saveError ? <p className="text-sm text-rose-600">{saveError}</p> : null}
            {saved ? <p className="text-sm text-emerald-600">Settings saved.</p> : null}

            <div className="flex justify-end">
              <Button type="button" onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save settings'}
              </Button>
            </div>
          </Card>
        ) : null}
      </DataState>
      )}
    </div>
  );
}
